import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FiMail, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import theme from '../styles/theme';
import { administradoresAPI } from '../services/api';

// Schema de validação
const loginSchema = yup.object({
  email: yup
    .string()
    .email('E-mail inválido')
    .required('E-mail é obrigatório'),
  password: yup
    .string()
    .min(1, 'Senha é obrigatória')
    .required('Senha é obrigatória'),
});

// Componentes estilizados
const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
`;

const LoginContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[4]};
`;

const LoginCard = styled.div`
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius['2xl']};
  box-shadow: ${theme.shadows['2xl']};
  padding: ${theme.spacing[8]};
  width: 100%;
  max-width: 400px;
  border: 1px solid ${theme.colors.border.primary};
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[8]};
`;

const LogoText = styled.h1`
  color: ${theme.colors.primary[500]};
  font-size: ${theme.fontSizes['3xl']};
  font-weight: 700;
  margin-bottom: ${theme.spacing[2]};
`;

const LogoSubtext = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[10]} ${theme.spacing[3]} ${theme.spacing[10]};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Icon = styled.div`
  position: absolute;
  left: ${theme.spacing[3]};
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.lg};
  z-index: 1;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${theme.spacing[3]};
  background: none;
  border: none;
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.lg};
  cursor: pointer;
  padding: ${theme.spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: ${theme.colors.text.secondary};
  }
`;

const ErrorMessage = styled.span`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.sm};
  margin-top: ${theme.spacing[1]};
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: ${theme.spacing[3]};
  background: ${theme.colors.primary[600]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[700]};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login, isLoading, isAuthenticated } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(loginSchema),
  });

  // Redirecionar se já estiver autenticado para a rota raiz
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);

  // Se não existir nenhum administrador, redirecionar para criação do primeiro
  useEffect(() => {
    const checkPrimeiroAdmin = async () => {
      try {
        const response = await administradoresAPI.existeAlgum();
        if (response.data?.success && response.data?.exists === false) {
          navigate('/setup/primeiro-admin', { replace: true });
        }
      } catch (e) {
        // silencioso: se falhar, segue fluxo normal de login
      }
    };
    checkPrimeiroAdmin();
  }, [navigate]);

  const onSubmit = async (data) => {
    const { email, password } = data;
    const result = await login(email, password);
    
    if (result.success) {
      // Redirecionar baseado no tipo de usuário
      if (result.user?.tipo === 'admin') {
        navigate('/dashboard/configuracoes', { replace: true });
      } else {
        navigate('/dashboard/creditos', { replace: true });
      }
    } else if (result.requiresPasswordReset) {
      // Redirecionar para tela de redefinição de senha
      navigate('/redefinir-senha', { replace: true });
    }
  };

  // Redirecionar imediatamente se já estiver autenticado (evita renderização desnecessária)
  if (!isLoading && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <LoginContainer>
      <LoginContent>
        <LoginCard>
          <Logo>
            <LogoText>Sistema de Gestão</LogoText>
            {/*          <LogoSubtext>Login</LogoSubtext> */}
          </Logo>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormGroup>
              <Label htmlFor="email">E-mail</Label>
              <InputContainer>
                <Icon>
                  <FiMail />
                </Icon>
                <Input
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  {...register('email')}
                  disabled={isLoading}
                />
              </InputContainer>
              {errors.email && (
                <ErrorMessage>{errors.email.message}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="password">Senha</Label>
              <InputContainer>
                <Icon>
                  <FiLock />
                </Icon>
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Sua senha"
                  {...register('password')}
                  disabled={isLoading}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </PasswordToggle>
              </InputContainer>
              {errors.password && (
                <ErrorMessage>{errors.password.message}</ErrorMessage>
              )}
            </FormGroup>

            <SubmitButton type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <LoadingSpinner />
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </SubmitButton>

            <RecuperarSenhaLink onClick={() => navigate('/recuperar-senha')}>
              Esqueceu sua senha?
            </RecuperarSenhaLink>
          </Form>
        </LoginCard>
      </LoginContent>
    </LoginContainer>
  );
};

const RecuperarSenhaLink = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary[500]};
  font-size: ${theme.fontSizes.sm};
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin-top: ${theme.spacing[2]};
  transition: color 0.2s ease;

  &:hover {
    color: ${theme.colors.primary[600]};
  }

  &:focus {
    outline: none;
  }
`;

export default Login;
